/**
 * Position Mapper - Bidirectional conversion between string and numeric positions
 *
 * Frontend uses string positions (e.g., 'GS2', 'YC3', 'BFINAL')
 * Backend Redis uses numeric positions (-1 for base, 0-55 for board, 56 for finish)
 *
 * This module provides utilities for converting between the two systems.
 */

import type { PlayerColor } from './game';

/**
 * Color prefix mapping for position strings
 */
const COLOR_TO_PREFIX: Record<PlayerColor, 'G' | 'Y' | 'B' | 'R'> = {
  green: 'G',
  yellow: 'Y',
  blue: 'B',
  red: 'R'
};

const PREFIX_TO_COLOR: Record<'G' | 'Y' | 'B' | 'R', PlayerColor> = {
  G: 'green',
  Y: 'yellow',
  B: 'blue',
  R: 'red'
};

/**
 * Start positions on the main board for each color
 * These are the numeric positions where pawns enter the board after release
 */
const COLOR_TO_START_NUMERIC: Record<PlayerColor, number> = {
  green: 0,
  yellow: 13,
  blue: 26,
  red: 39
};

/**
 * Section paths for each color
 * Defines the order of positions in each section's roads
 */
const SECTION_PATHS = {
  G: {
    start: ['GS1', 'GS2', 'GS3', 'GS4', 'GS5', 'GS6'],
    final: ['GF6', 'GF5', 'GF4', 'GF3', 'GF2', 'GF1'],
    center: ['GC1', 'GC2', 'GC3', 'GC4', 'GC5', 'GC6'],
    finalPosition: 'GFINAL'
  },
  Y: {
    start: ['YS1', 'YS2', 'YS3', 'YS4', 'YS5', 'YS6'],
    final: ['YF6', 'YF5', 'YF4', 'YF3', 'YF2', 'YF1'],
    center: ['YC1', 'YC2', 'YC3', 'YC4', 'YC5', 'YC6'],
    finalPosition: 'YFINAL'
  },
  B: {
    start: ['BS1', 'BS2', 'BS3', 'BS4', 'BS5', 'BS6'],
    final: ['BF6', 'BF5', 'BF4', 'BF3', 'BF2', 'BF1'],
    center: ['BC1', 'BC2', 'BC3', 'BC4', 'BC5', 'BC6'],
    finalPosition: 'BFINAL'
  },
  R: {
    start: ['RS1', 'RS2', 'RS3', 'RS4', 'RS5', 'RS6'],
    final: ['RF6', 'RF5', 'RF4', 'RF3', 'RF2', 'RF1'],
    center: ['RC1', 'RC2', 'RC3', 'RC4', 'RC5', 'RC6'],
    finalPosition: 'RFINAL'
  }
} as const;

/**
 * Section order for clockwise traversal around the board
 */
const SECTION_ORDER = ['G', 'Y', 'B', 'R'] as const;

/**
 * Build the complete forward path for a given color starting from their start position
 * This creates the circuit path that a pawn follows from start to final
 *
 * @param colorPrefix - The color section prefix ('G', 'Y', 'B', or 'R')
 * @returns Array of all positions in order that a pawn will traverse
 */
function buildForwardPath(colorPrefix: 'G' | 'Y' | 'B' | 'R'): string[] {
  const path: string[] = [];
  const startSectionIndex = SECTION_ORDER.indexOf(colorPrefix);

  // Add positions for the current section's start road (from S2 onwards, as S1 is the entry point)
  const homeSection = SECTION_PATHS[colorPrefix];
  path.push(...homeSection.start.slice(1)); // S2 to S6

  // Traverse through the next 3 sections in clockwise order
  for (let i = 1; i <= 3; i++) {
    const sectionIndex = (startSectionIndex + i) % 4;
    const section = SECTION_PATHS[SECTION_ORDER[sectionIndex]];

    // Add circuit through this section: F6 -> F5 -> ... -> F1 -> C1 -> S1 -> S2 -> ... -> S6
    path.push(...section.final); // F6 to F1
    path.push(section.center[0]); // C1 (transition point)
    path.push(...section.start); // S1 to S6
  }

  // After completing the circuit, enter home section's final road
  path.push(...homeSection.final); // F6 to F1

  // Then enter center path to final position
  path.push(...homeSection.center); // C1 to C6
  path.push(homeSection.finalPosition); // FINAL

  return path;
}

/**
 * Pre-built path cache for each color
 * Paths are built once at module initialization for O(1) lookup performance
 */
const PATH_CACHE: Record<'G' | 'Y' | 'B' | 'R', string[]> = {
  G: buildForwardPath('G'),
  Y: buildForwardPath('Y'),
  B: buildForwardPath('B'),
  R: buildForwardPath('R')
};

/**
 * Convert a string position to a numeric position for Redis storage
 *
 * Position values:
 * - -1: Pawn in base (not released)
 * - 0-49: Pawn on main board path
 * - 50-55: Pawn in center corridor (XC1-XC6)
 * - 56: Pawn at FINAL position
 *
 * @param position - String position (e.g., 'GS2', 'YC3', 'BFINAL')
 * @param color - The player's color
 * @returns Numeric position for Redis storage
 * @throws Error if position is invalid
 */
export function positionToNumeric(position: string, color: PlayerColor): number {
  // Handle base position
  if (position === 'BASE' || position === '-1') {
    return -1;
  }

  const colorPrefix = COLOR_TO_PREFIX[color];
  const forwardPath = PATH_CACHE[colorPrefix];

  // Find position in the forward path
  const index = forwardPath.indexOf(position);

  if (index === -1) {
    throw new Error(`Invalid position: ${position} not found in path for color ${color}`);
  }

  // The forward path starts at S2 (index 0 in path = position 0 on board)
  // Main board path: 0-49 (50 positions)
  // Center corridor: 50-55 (6 positions)
  // FINAL: 56

  return index;
}

/**
 * Convert a numeric position to a string position for Ably events and frontend
 *
 * @param numeric - Numeric position from Redis
 * @param color - The player's color
 * @returns String position (e.g., 'GS2', 'YC3', 'BFINAL')
 * @throws Error if numeric position is out of valid range
 */
export function numericToPosition(numeric: number, color: PlayerColor): string {
  // Handle base position
  if (numeric === -1) {
    return 'BASE';
  }

  const colorPrefix = COLOR_TO_PREFIX[color];
  const forwardPath = PATH_CACHE[colorPrefix];

  // Validate numeric is within bounds
  if (numeric < 0 || numeric >= forwardPath.length) {
    throw new Error(
      `Invalid numeric position: ${String(numeric)} for color ${color}. Valid range: -1 to ${String(forwardPath.length - 1)}`
    );
  }

  return forwardPath[numeric];
}

/**
 * Check if a numeric position is in the center corridor (50-55)
 *
 * @param numeric - Numeric position
 * @returns True if in center corridor
 */
export function isNumericInCenterCorridor(numeric: number): boolean {
  // Center corridor positions are index 50-55 (XC1-XC6)
  // FINAL is at index 56
  return numeric >= 50 && numeric <= 55;
}

/**
 * Check if a string position is in the center corridor (XC1-XC6)
 *
 * @param position - String position
 * @returns True if in center corridor
 */
export function isStringInCenterCorridor(position: string): boolean {
  const pattern = /^[GYBR]C[1-6]$/;
  return pattern.test(position);
}

/**
 * Get the start position string for a color
 *
 * @param color - Player color
 * @returns Start position string (e.g., 'GS2' for green)
 */
export function getStartPositionString(color: PlayerColor): string {
  const prefix = COLOR_TO_PREFIX[color];
  return `${prefix}S2`;
}

/**
 * Get the start position numeric for a color
 *
 * @param color - Player color
 * @returns Start position numeric (e.g., 0 for green)
 */
export function getStartPositionNumeric(color: PlayerColor): number {
  return COLOR_TO_START_NUMERIC[color];
}

/**
 * Get the FINAL position string for a color
 *
 * @param color - Player color
 * @returns FINAL position string (e.g., 'GFINAL' for green)
 */
export function getFinalPositionString(color: PlayerColor): string {
  const prefix = COLOR_TO_PREFIX[color];
  return `${prefix}FINAL`;
}

/**
 * Check if a string position is a FINAL position
 *
 * @param position - String position
 * @returns True if FINAL position
 */
export function isFinalPosition(position: string): boolean {
  return position.endsWith('FINAL');
}

// Export for testing
export { COLOR_TO_PREFIX, PREFIX_TO_COLOR, PATH_CACHE };
